// src/driveBackup.js
// Drive API + token helpers for the auto-backup feature. Kept separate from
// Chat.js (already large) since this is a self-contained concern: a
// Drive-only OAuth grant, independent of the Canjur login, and the Drive
// calls needed to mirror a conversation into "Chat Backups/<chat name>".

const DRIVE_FILES_URL   = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD_URL  = 'https://www.googleapis.com/upload/drive/v3/files';
const TOKEN_EXPIRY_SKEW_MS = 60 * 1000; // refresh a bit before actual expiry

export function isBackupConnected() {
  return !!localStorage.getItem('pm_backup_gdrive_refresh');
}

export function getBackupAccountEmail() {
  return localStorage.getItem('pm_backup_gdrive_email') || '';
}

// Clears cached Drive folder/file IDs. These belong to whichever account
// was last connected and would be invalid (404) under a different account,
// so this runs both on explicit disconnect and before starting a fresh
// connect/switch-account OAuth round trip.
export function clearBackupSyncCache() {
  ['pm_gdrive_backup_root_id', 'pm_gdrive_backup_root_id_v2', 'pm_gdrive_backup_map']
    .forEach(k => localStorage.removeItem(k));
}

export function disconnectBackup() {
  ['pm_backup_gdrive_token', 'pm_backup_gdrive_refresh', 'pm_backup_gdrive_expiry', 'pm_backup_gdrive_email']
    .forEach(k => localStorage.removeItem(k));
  clearBackupSyncCache();
}

// Ensures a live Drive access token, refreshing via the server-side
// refresh.js endpoint if the cached one is expired/near-expiry. Returns the
// access token, or null if there's no connection / the refresh token is dead.
export async function ensureBackupToken() {
  const refreshToken = localStorage.getItem('pm_backup_gdrive_refresh');
  if (!refreshToken) return null;

  const token  = localStorage.getItem('pm_backup_gdrive_token');
  const expiry = Number(localStorage.getItem('pm_backup_gdrive_expiry') || 0);

  if (token && Date.now() < expiry - TOKEN_EXPIRY_SKEW_MS) {
    return token;
  }

  try {
    const res = await fetch('/api/auth/google-drive/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    const data = await res.json();
    if (!res.ok || !data.access_token) {
      // Refresh token revoked/invalid -- caller should prompt reconnect.
      disconnectBackup();
      return null;
    }
    const newExpiry = Date.now() + (Number(data.expires_in) || 3600) * 1000;
    localStorage.setItem('pm_backup_gdrive_token', data.access_token);
    localStorage.setItem('pm_backup_gdrive_expiry', String(newExpiry));
    return data.access_token;
  } catch {
    return null;
  }
}

export function sanitizeDriveName(title) {
  const cleaned = (title || '').replace(/[\\/]/g, '-').trim();
  return cleaned || 'Untitled chat';
}

export function hashText(text) {
  // Small non-cryptographic hash (djb2) -- only used to detect whether a
  // conversation changed since the last sync, not for security.
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
}

// v2 key: earlier builds cached a top-level "Chat Backups" folder (no Canjur
// parent) under 'pm_gdrive_backup_root_id'. Using a new key name here means
// that stale cache is simply ignored -- forcing one fresh
// find-or-create pass under the new Canjur/Chat Backups nesting -- instead
// of silently reusing the old top-level folder forever.
export function getBackupRootFolderId() {
  return localStorage.getItem('pm_gdrive_backup_root_id_v2') || null;
}

export function setBackupRootFolderId(id) {
  localStorage.setItem('pm_gdrive_backup_root_id_v2', id);
}

// Ensures "Canjur/Chat Backups" exists (creating either level as needed) and
// returns the "Chat Backups" folder's fileId, caching it so this only runs
// the Drive lookups once per browser/account.
export async function ensureBackupRootFolder(token) {
  const cached = getBackupRootFolderId();
  if (cached) return cached;
  const canjurId = await findOrCreateFolder(token, 'Canjur', null);
  const chatBackupsId = await findOrCreateFolder(token, 'Chat Backups', canjurId);
  setBackupRootFolderId(chatBackupsId);
  return chatBackupsId;
}

export function getBackupMap() {
  try {
    return JSON.parse(localStorage.getItem('pm_gdrive_backup_map') || '{}');
  } catch {
    return {};
  }
}

export function setBackupMapEntry(conversationId, entry) {
  const map = getBackupMap();
  map[conversationId] = { ...map[conversationId], ...entry };
  localStorage.setItem('pm_gdrive_backup_map', JSON.stringify(map));
}

async function driveFetch(token, url, options = {}) {
  return fetch(url, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
}

// Finds a non-trashed folder by name (optionally scoped to a parent),
// creating it if it doesn't exist. Returns the folder's fileId.
export async function findOrCreateFolder(token, name, parentId) {
  const escaped = name.replace(/'/g, "\\'");
  let q = `name='${escaped}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;
  if (parentId) q += ` and '${parentId}' in parents`;

  const listRes = await driveFetch(
    token,
    `${DRIVE_FILES_URL}?q=${encodeURIComponent(q)}&fields=files(id,name)&spaces=drive`
  );
  const listData = await listRes.json();
  if (listData.files && listData.files.length > 0) {
    return listData.files[0].id;
  }

  const createRes = await driveFetch(token, DRIVE_FILES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: parentId ? [parentId] : undefined,
    }),
  });
  const created = await createRes.json();
  return created.id;
}

// Creates (multipart) or updates (media PATCH) a markdown file's content.
// Returns the resulting fileId.
export async function uploadOrUpdateFile(token, { fileId, folderId, filename, content }) {
  const blob = new Blob([content], { type: 'text/markdown' });

  if (fileId) {
    const res = await driveFetch(
      token,
      `${DRIVE_UPLOAD_URL}/${fileId}?uploadType=media`,
      { method: 'PATCH', headers: { 'Content-Type': 'text/markdown' }, body: blob }
    );
    if (!res.ok) throw new Error(`Drive file update failed: ${res.status}`);
    const data = await res.json();
    return data.id;
  }

  const metadata = { name: filename, parents: [folderId] };
  const boundary = 'pm_backup_boundary_' + Date.now();
  const body =
    `--${boundary}\r\n` +
    `Content-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: text/markdown\r\n\r\n${content}\r\n` +
    `--${boundary}--`;

  const res = await driveFetch(
    token,
    `${DRIVE_UPLOAD_URL}?uploadType=multipart`,
    { method: 'POST', headers: { 'Content-Type': `multipart/related; boundary=${boundary}` }, body }
  );
  if (!res.ok) throw new Error(`Drive file create failed: ${res.status}`);
  const data = await res.json();
  return data.id;
}

async function renameDriveFile(token, fileId, newName) {
  await driveFetch(token, `${DRIVE_FILES_URL}/${fileId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: newName }),
  });
}

// Orchestrates: find-or-create "<chat name>" subfolder under the "Chat
// Backups" root (renaming it in place if the conversation's title changed
// since the last sync), then upload/update chat.md inside it. Uses/updates
// the cached folderId/fileId/title in pm_gdrive_backup_map so repeat syncs
// don't re-search Drive or create duplicate folders/files.
export async function syncConversationToDrive(token, rootFolderId, conversation, markdownText) {
  const map = getBackupMap();
  const cached = map[conversation.id] || {};
  const name = sanitizeDriveName(conversation.title);

  let folderId = cached.folderId;
  if (folderId && cached.title !== conversation.title) {
    await renameDriveFile(token, folderId, name);
  } else if (!folderId) {
    folderId = await findOrCreateFolder(token, name, rootFolderId);
  }

  const fileId = await uploadOrUpdateFile(token, {
    fileId: cached.fileId,
    folderId,
    filename: 'chat.md',
    content: markdownText,
  });

  setBackupMapEntry(conversation.id, {
    folderId,
    fileId,
    title: conversation.title,
    contentHash: hashText(markdownText),
    lastSyncedAt: new Date().toISOString(),
  });
}
